/**
 * Outlook / M365 on Mac often pastes email HTML with `<img src="cid:…">`
 * and the actual PNG/JPEG bytes only in `text/rtf` `\pict` groups — no
 * clipboard File items. Extract those rasters so mix-paste can rehost them.
 */

const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47];
const JPEG_MAGIC = [0xff, 0xd8, 0xff];

function bytesStartWith(bytes: Uint8Array, magic: number[]): boolean {
  if (bytes.length < magic.length) {
    return false;
  }
  return magic.every((value, index) => bytes[index] === value);
}

function hexToBytes(hex: string): Uint8Array | null {
  const compact = hex.replace(/[^0-9a-fA-F]/g, '');
  if (compact.length < 16 || compact.length % 2 !== 0) {
    return null;
  }
  const bytes = new Uint8Array(compact.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    const pair = compact.slice(index * 2, index * 2 + 2);
    const value = Number.parseInt(pair, 16);
    if (Number.isNaN(value)) {
      return null;
    }
    bytes[index] = value;
  }
  return bytes;
}

/** Skip `\nonshppict{…}` WMF fallbacks so we prefer `\*\shppict` PNG/JPEG. */
function stripNonshppictGroups(rtf: string): string {
  let out = rtf;
  for (let guard = 0; guard < 64; guard += 1) {
    const start = out.indexOf('{\\nonshppict');
    if (start < 0) {
      break;
    }
    const group = extractBraceGroup(out, start);
    if (!group) {
      break;
    }
    out = out.slice(0, start) + out.slice(start + group.length);
  }
  return out;
}

function extractBraceGroup(source: string, openIndex: number): string | null {
  if (source[openIndex] !== '{') {
    return null;
  }
  let depth = 0;
  for (let index = openIndex; index < source.length; index += 1) {
    const ch = source[index];
    if (ch === '{') {
      depth += 1;
    } else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        return source.slice(openIndex, index + 1);
      }
    }
  }
  return null;
}

function findPictGroups(rtf: string): string[] {
  const groups: string[] = [];
  let searchFrom = 0;
  while (searchFrom < rtf.length) {
    const marker = rtf.indexOf('{\\pict', searchFrom);
    if (marker < 0) {
      break;
    }
    const group = extractBraceGroup(rtf, marker);
    if (!group) {
      break;
    }
    groups.push(group);
    searchFrom = marker + group.length;
  }
  return groups;
}

function hexAfterBlip(
  pictGroup: string,
  blipWord: '\\pngblip' | '\\jpegblip',
): string {
  const at = pictGroup.indexOf(blipWord);
  if (at < 0) {
    return '';
  }
  let rest = pictGroup.slice(at + blipWord.length);
  if (rest.endsWith('}')) {
    rest = rest.slice(0, -1);
  }
  let index = 0;
  while (index < rest.length) {
    const ch = rest[index];
    if (ch === '\\') {
      index += 1;
      while (index < rest.length && /[a-zA-Z]/.test(rest[index])) {
        index += 1;
      }
      if (rest[index] === '-') {
        index += 1;
      }
      while (index < rest.length && /[0-9]/.test(rest[index])) {
        index += 1;
      }
      if (rest[index] === ' ') {
        index += 1;
      }
      continue;
    }
    if (/\s/.test(ch)) {
      index += 1;
      continue;
    }
    break;
  }
  return rest.slice(index);
}

/**
 * Pull PNG/JPEG rasters from Outlook `text/rtf`. Ignores WMF/`wmetafile` picts.
 */
export function extractRtfClipboardImages(rtf: string): File[] {
  if (!rtf || !rtf.includes('\\pict')) {
    return [];
  }
  const files: File[] = [];
  const pictGroups = findPictGroups(stripNonshppictGroups(rtf));
  let index = 0;
  for (const group of pictGroups) {
    const kind: 'png' | 'jpeg' | null = group.includes('\\pngblip')
      ? 'png'
      : group.includes('\\jpegblip')
      ? 'jpeg'
      : null;
    if (!kind) {
      continue;
    }
    const blipWord = kind === 'png' ? '\\pngblip' : '\\jpegblip';
    const bytes = hexToBytes(hexAfterBlip(group, blipWord));
    if (!bytes) {
      continue;
    }
    const magicOk =
      kind === 'png'
        ? bytesStartWith(bytes, PNG_MAGIC)
        : bytesStartWith(bytes, JPEG_MAGIC);
    if (!magicOk) {
      continue;
    }
    const mime = kind === 'png' ? 'image/png' : 'image/jpeg';
    const extension = kind === 'png' ? 'png' : 'jpg';
    files.push(
      new File([bytes], `pasted-rtf-${index}.${extension}`, { type: mime }),
    );
    index += 1;
  }
  return files;
}
